import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Camera,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  KeyRound,
  LogOut,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/toaster';
import { useAuth, useSessionUser } from '@/lib/auth';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// The account menu, and the two things it opens.
//
// Split out of `Topbar` because it now owns real behaviour - an upload and a
// credential change - rather than a link and a sign-out.
// -----------------------------------------------------------------------------

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: z
      .string()
      .min(8, 'Use at least 8 characters.')
      .max(128, 'That is longer than 128 characters.'),
    confirmPassword: z.string().min(1, 'Repeat the new password.'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'The two passwords do not match.',
    path: ['confirmPassword'],
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    message: 'That is the password you already have.',
    path: ['newPassword'],
  });

type PasswordValues = z.infer<typeof passwordSchema>;

export function AccountMenu(): JSX.Element {
  const user = useSessionUser();
  const { signOut } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const isSuperAdmin = user.role === 'super_admin';
  const roleLabel = isSuperAdmin ? 'Super administrator' : 'Administrator';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex items-center gap-2.5 rounded-md py-1 pl-1 pr-2 text-left transition-colors',
              'hover:bg-canvas',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            )}
          >
            <AccountAvatar className="size-7 ring-1 ring-border" />

            <span className="hidden min-w-0 md:block">
              <span className="block max-w-[150px] truncate text-sm font-semibold leading-tight text-foreground">
                {user.displayName ?? user.email?.split('@')[0] ?? 'Staff'}
              </span>
              <span className="block truncate text-[11px] leading-tight text-muted-foreground">
                {roleLabel}
              </span>
            </span>

            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64">
          <div className="flex items-center gap-3 p-2">
            <AccountAvatar className="size-10 ring-1 ring-border" />
            <div className="min-w-0">
              <p className="truncate font-semibold leading-tight text-foreground">
                {user.displayName ?? 'Staff'}
              </p>
              <p className="truncate text-sm leading-tight text-muted-foreground">{user.email}</p>
            </div>
          </div>

          <div className="px-2 pb-2">
            <Badge variant={isSuperAdmin ? 'brand' : 'info'}>
              <ShieldCheck className="size-3" aria-hidden />
              {roleLabel}
            </Badge>
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={() => setProfileOpen(true)}>
            <UserRound aria-hidden />
            Profile photo
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setPasswordOpen(true)}>
            <KeyRound aria-hidden />
            Change password
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={() => void signOut()}>
            <LogOut aria-hidden />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ProfilePhotoDialog open={profileOpen} onOpenChange={setProfileOpen} />
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </>
  );
}

/** Avatar with initials fallback, shared by the trigger and the menu header. */
function AccountAvatar({ className }: { className?: string }): JSX.Element {
  const user = useSessionUser();

  const initials =
    (user.displayName ?? user.email ?? '?')
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?';

  return (
    <Avatar className={className}>
      {user.photoURL ? <AvatarImage src={user.photoURL} alt="" /> : null}
      <AvatarFallback>{initials}</AvatarFallback>
    </Avatar>
  );
}

/**
 * Avatar upload.
 *
 * Uploads to `users/{uid}/profile/…`, which the existing Storage rules already
 * permit for the owning user. A local preview is shown immediately so the choice
 * is confirmed before the upload finishes - and revoked on unmount, because an
 * object URL that is never released leaks the whole file.
 */
function ProfilePhotoDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const user = useSessionUser();
  const { updateAvatar } = useAuth();
  const toast = useToast();

  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = (): void => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setFile(null);
    setError(null);
  };

  const choose = (chosen: File | undefined): void => {
    if (!chosen) return;
    setError(null);
    if (preview) URL.revokeObjectURL(preview);
    setFile(chosen);
    setPreview(URL.createObjectURL(chosen));
  };

  const save = async (): Promise<void> => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await updateAvatar(file);
      toast.success('Photo updated', 'Your new picture is live across the console.');
      reset();
      onOpenChange(false);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error ? uploadError.message : 'Could not upload that image.',
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (uploading && !next) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Profile photo</DialogTitle>
          <DialogDescription>
            Shown on your account menu and beside your entries in the audit log.
            JPEG, PNG, WebP or HEIC, up to 8 MB.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-5">
          <div className="relative">
            <Avatar className="size-20 ring-1 ring-border">
              {preview ?? user.photoURL ? (
                <AvatarImage src={preview ?? user.photoURL ?? ''} alt="" />
              ) : null}
              <AvatarFallback className="text-xl">
                {(user.displayName ?? user.email ?? '?').slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>

            {preview ? (
              <span
                className="absolute -bottom-1 -right-1 flex size-6 items-center justify-center rounded-full bg-success ring-2 ring-background"
                aria-hidden
              >
                <Check className="size-3.5 text-white" />
              </span>
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              className="sr-only"
              onChange={(event) => choose(event.target.files?.[0])}
            />

            <Button
              type="button"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              <Camera aria-hidden />
              {preview ? 'Choose another' : 'Choose image'}
            </Button>

            {file ? (
              <p className="mt-2 truncate text-sm text-muted-foreground">
                {file.name} · {(file.size / 1024).toFixed(0)} KB
              </p>
            ) : null}
          </div>
        </div>

        {error ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={uploading} disabled={!file}>
            Save photo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Change password.
 *
 * Requires the CURRENT password before setting a new one. Firebase demands a
 * recent login for `updatePassword`, but the real reason is that a session left
 * open on an unlocked machine must not be enough to take an admin account over.
 */
function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { changePassword, sendPasswordReset } = useAuth();
  const user = useSessionUser();
  const toast = useToast();

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      await changePassword(values.currentPassword, values.newPassword);
      toast.success('Password changed', 'Use the new one next time you sign in.');
      reset();
      onOpenChange(false);
    } catch (changeError) {
      setFailure(
        changeError instanceof Error ? changeError.message : 'Could not change your password.',
      );
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isSubmitting && !next) return;
        if (!next) {
          reset();
          setFailure(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>
            Confirm the password you have now, then choose a new one.
          </DialogDescription>
        </DialogHeader>

        {failure ? (
          <div
            role="alert"
            className="rounded-lg border border-destructive/25 bg-danger-soft p-3 text-sm font-medium text-destructive"
          >
            {failure}
          </div>
        ) : null}

        <form id="change-password" onSubmit={onSubmit} noValidate className="flex flex-col gap-3.5">
          <PasswordField
            id="currentPassword"
            label="Current password"
            autoComplete="current-password"
            show={showCurrent}
            onToggle={() => setShowCurrent((value) => !value)}
            error={errors.currentPassword?.message}
            disabled={isSubmitting}
            register={register('currentPassword')}
          />

          <PasswordField
            id="newPassword"
            label="New password"
            autoComplete="new-password"
            hint="At least 8 characters."
            show={showNew}
            onToggle={() => setShowNew((value) => !value)}
            error={errors.newPassword?.message}
            disabled={isSubmitting}
            register={register('newPassword')}
          />

          <PasswordField
            id="confirmPassword"
            label="Confirm new password"
            autoComplete="new-password"
            show={showNew}
            onToggle={() => setShowNew((value) => !value)}
            error={errors.confirmPassword?.message}
            disabled={isSubmitting}
            register={register('confirmPassword')}
          />
        </form>

        {/* The escape hatch for someone who cannot remember the current one.
            Same Firebase-delivered email the sign-in screen sends. */}
        <button
          type="button"
          disabled={isSubmitting}
          onClick={async () => {
            try {
              if (user.email) await sendPasswordReset(user.email);
              toast.success('Reset link sent', `Check ${user.email} for the link.`);
              onOpenChange(false);
            } catch (resetError) {
              toast.failure(resetError, 'Could not send the reset email');
            }
          }}
          className="self-start rounded text-sm font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
        >
          Forgot it? Email me a reset link instead
        </button>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="change-password" loading={isSubmitting}>
            {isSubmitting ? 'Updating…' : 'Update password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordField({
  id,
  label,
  hint,
  autoComplete,
  show,
  onToggle,
  error,
  disabled,
  register,
}: {
  id: string;
  label: string;
  hint?: string;
  autoComplete: string;
  show: boolean;
  onToggle: () => void;
  error?: string;
  disabled?: boolean;
  register: ReturnType<ReturnType<typeof useForm<PasswordValues>>['register']>;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>

      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          className="pr-10"
          {...register}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? 'Hide password' : 'Show password'}
          disabled={disabled}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          {show ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
        </button>
      </div>

      {error ? (
        <p className="text-sm font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-sm text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
